import {
  Component,
  EventEmitter,
  OnDestroy, Output,
  Input
} from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import {
  ColDef,
  GridOptions,
  GridApi,
  IServerSideGetRowsParams,
  GridReadyEvent,
  IServerSideDatasource,
  CellClickedEvent,
  ICellRendererParams,
} from 'ag-grid-community';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

//COMPONENTS
import { RfqFormComponent, RfqVariantFormComponent } from '../../../rfq';

//SERVICES

import { DialogConfig, SnackBarConfig } from '../../../shared/configs';
import { LocalStorageKeys } from '../../../shared/interfaces';
import {
  ChannelApiService,
  ChannelProductMappingApiService,
  LoadingDialogUtilService,
  RfqApiService,
} from '../../../shared/services';

//INTERFACES
import {
  EChannelProductAgField,
  EChannelProductAgFilter,
  IChannelProduct,
  IChannelProductMappingFeatureFlag,
  IChannelProductRequestFilter,
} from '../../interfaces';
import { ChannelProductMappingActionCellRendererComponent } from '../channel-product-mapping-action-cell-renderer/channel-product-mapping-action-cell-renderer.component';
import { ChannelProductMappingCheckboxCellRendererComponent } from '../channel-product-mapping-checkbox-cell-renderer/channel-product-mapping-checkbox-cell-renderer.component';
import { ChannelProductMappingSearchSkuCellEditorComponent } from '../channel-product-mapping-search-sku-cell-editor/channel-product-mapping-search-sku-cell-editor.component';
import { ChannelProductSyncProductsFormComponent } from '../channel-product-sync-products-form/channel-product-sync-products-form.component';

@Component({
  selector: 'lum-channel-product-mapping',
  templateUrl: './channel-product-mapping.component.html',
  styleUrls: ['./channel-product-mapping.component.scss'],
})
export class ChannelProductMappingComponent implements OnDestroy {
  @Input()
  featureFlags :IChannelProductMappingFeatureFlag;

  @Output()
  onSyncProducts = new EventEmitter<boolean>();

  columnDefs :ColDef[] = [
    { headerCheckboxSelection: true, checkboxSelection: true, width: 50 },
    {
      headerName: 'ID',
      field: EChannelProductAgField.ID,
      flex: 1,
      sortable: true,
      minWidth: 150,
    },
    {
      headerName: 'Channel SKU',
      field: EChannelProductAgField.SHOPIFY_SKU,
      flex: 1,
      sortable: true,
      minWidth: 200,
    },
    {
      headerName: 'Channel Product Name',
      field: EChannelProductAgField.PRODUCT_NAME,
      flex: 1,
      sortable: true,
      minWidth: 200,
    },
    {
      headerName: 'LUM SKU',
      field: EChannelProductAgField.RFQ,
      flex: 1,
      minWidth: 200,
      filter: true,
      floatingFilter: true,
      filterParams: {
        values: [EChannelProductAgFilter.MAPPED, EChannelProductAgFilter.UNMAPPED],
        debounceMs: 1000,
      },
      cellEditorFramework: ChannelProductMappingSearchSkuCellEditorComponent,
      singleClickEdit: true,
      editable: true,
      onCellClicked: (params :CellClickedEvent) => {
        if (params?.value && params.node?.rowIndex) {
          params.api.startEditingCell({
            rowIndex: params.node.rowIndex,
            colKey: params.column.getColId(),
          });
        }
      },
      cellRenderer: (params :ICellRendererParams) => {
        if (params.value?.id) {
          const { internalSku, productName } = params.value;

          return `[${internalSku || 'N/A'}] ${productName}`;
        }

        return '<i>Click to search sku</i>';
      },
    },
    {
      headerName: 'Channel',
      field: EChannelProductAgField.CHANNEL,
      flex: 1,
      sortable: true,
      minWidth: 150,
      cellRenderer: (params :any) => {
        return params.value.label;
      },
    },
    {
      headerName: 'Product Status',
      field: EChannelProductAgField.PRODUCT_FLAG_STATUS,
      filter: true,
      filterParams: {
        values: [EChannelProductAgFilter.ACTIVE, EChannelProductAgFilter.INACTIVE],
        debounceMs: 1000,
      },
      flex: 1,
      sortable: true,
      minWidth: 150,
      floatingFilter: true,
    },
    {
      headerName: 'Last Sync Status',
      field: EChannelProductAgField.SYNC_STATUS,
      flex: 1,
      sortable: true,
      minWidth: 150,
    },
    {
      headerName: 'Ignore',
      field: EChannelProductAgField.IGNORED,
      cellRenderer: ChannelProductMappingCheckboxCellRendererComponent,
      width: 100,
    },
    {
      headerName: 'Actions',
      field: 'actions',
      pinned: 'right',
      cellClass: 'no-border-highlight',
      cellRenderer: ChannelProductMappingActionCellRendererComponent,
      cellRendererParams: {
        onCreateSku: (data :IChannelProduct) => this.createSku(data),
        onCreateVariantSku: (data :IChannelProduct) => this.addVariantSku(data),
        onAddAlternateSku: (data :IChannelProduct) => this.addAlternateSku(data),
      },
      width: 200,
    },
  ];

  gridOptions :GridOptions = {
    rowModelType: 'serverSide',
    pagination: true,
    cacheBlockSize: 100,
    maxBlocksInCache: 100,
    paginationPageSize: 100,
    domLayout: 'autoHeight',
    sideBar: {
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressRowGroups: true,
          },
        },
      ],
      defaultToolPanel: '',
    },
  };

  private gridApi :GridApi;
  private onDestroy$ = new Subject();
  private latestRequestParams :IServerSideGetRowsParams;
  public downloadingState :boolean = false;

  constructor(
    private channelProductMappingApiService :ChannelProductMappingApiService,
    private dialog :MatDialog,
    private rfqApiService :RfqApiService,
    private snackBar :MatSnackBar,
    private channelApiService :ChannelApiService,
    private loadingDialogUtilService :LoadingDialogUtilService,
  ) {}

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  get nonStickyColumnsWidth() :number {
    const columnWidth = 100;
    const numberOfNonStickyColumns = this.columnDefs.length - 1;

    return columnWidth * numberOfNonStickyColumns;
  }

  onGridReady(params :GridReadyEvent) :void {
    params.api.setFilterModel({
      rfq: {
        type: 'set',
        values: [EChannelProductAgFilter.UNMAPPED],
      },
      productFlagStatus: {
        type: 'set',
        values: [EChannelProductAgFilter.ACTIVE],
      },
    });

    this.gridApi = params.api;
    const datasource = this.getServerSideDatasource();
    this.gridApi.setServerSideDatasource(datasource);

    this.applySavedPageState();
    this.attachEventListenersOnPageStateChange();
  }

  getServerSideDatasource() :IServerSideDatasource {
    return {
      getRows: (params :IServerSideGetRowsParams) => {
        this.latestRequestParams = params;
        this.channelProductMappingApiService
          .getProducts(this.mapRequestParams(params))
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((response) => {
            params.successCallback(response.data, response.meta.total);
          });
      },
    };
  }

  createSku(data :IChannelProduct) :void {
    this.dialog
      .open(RfqFormComponent, {
        data: {
          featureFlag: {
            newKitsStructureEnabled:
              this.featureFlags?.newKitsStructureEnabled || false,
            lotTrackingEnabled: this.featureFlags?.lotTrackingEnabled || false,
            customFieldsEnabled: this.featureFlags?.customFieldsEnabled || false,
          },
          permissions: [],
          initialData: {
            internal_sku: data.shopifySku,
            product_name: data.productName,
          },
        },
        width: DialogConfig.sizes['xx-large'],
        height: 'calc(100vh - 150px)',
        disableClose: true,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe();
  }

  addVariantSku(data :IChannelProduct) :void {
    this.dialog
      .open(RfqVariantFormComponent, {
        data: {
          featureFlag: {
            newKitsStructureEnabled:
              this.featureFlags?.newKitsStructureEnabled || false,
            lotTrackingEnabled: this.featureFlags?.lotTrackingEnabled || false,
          },
        },
        width: DialogConfig.sizes.medium,
        disableClose: true,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe();
  }

  addAlternateSku(data :IChannelProduct) :void {
    this.rfqApiService
      .addAlternateSku({
        rfqId: data.rfq.id,
        sku: data.rfq.internalSku,
        alternateSku: data.shopifySku,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          this.gridApi.refreshServerSide();
          this.snackBar.open(`Alternate sku successfully added.`, 'Success', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
        error: (response) => {
          const errorMessage = response.error ?? 'Failed to add alternate sku.';
          this.snackBar.open(errorMessage, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  onOpenSyncProductsModal() :void {
    this.dialog
      .open(ChannelProductSyncProductsFormComponent, {
        width: DialogConfig.sizes.small,
        data: {},
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((params :any) => {
        if (params) {
          if (params.channelIds.length > 0) {
            params.channelIds = params.channelIds.join(',');
          }
          this.syncProductsByChannelIds(params);
        }
      });
  }

  // TODO: on select rows action
  onSelectionChanged() :void {}

  private syncProductsByChannelIds(params :{
    [param :string] :string | string[];
  }) :void {
    this.channelApiService
      .syncProductsByChannelIds(params)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :any) => {
          this.snackBar.open(response.success, 'Success', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
          this.onSyncProducts.emit(true);
        },
        error: (error :any) => {
          this.snackBar.open('Failed to sync products.', 'Error', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private mapRequestParams(params :IServerSideGetRowsParams) :{
    [param :string] :string | string[];
  } {
    const filters :IChannelProductRequestFilter = {};
    const relations =
      'rfq.unitOfMeasures,rfq.alternateSkus,location,channel,defaultSellingUnit';
    const limit = this.gridOptions.paginationPageSize || 10;
    const page = params?.request?.startRow
      ? params.request.startRow / limit + 1
      : 1;

    let sortDirection = '';
    let sortField = '';

    if (params?.request?.sortModel && params?.request?.sortModel?.length > 0) {
      const sortModel = params.request.sortModel[0];
      sortDirection = sortModel.sort;
      sortField = this.mapSortField(sortModel.colId);
    }

    if (
      params?.request?.filterModel &&
      params?.request?.filterModel?.productFlagStatus
    ) {
      const productFlagStatusFilter =
        params.request.filterModel.productFlagStatus.values[0];
      filters.product_flag_status = productFlagStatusFilter.toLowerCase();
    }

    if (params?.request?.filterModel && params?.request?.filterModel?.rfq) {
      const mappedFilter = params.request.filterModel.rfq.values[0];
      filters.mapped_to =
        mappedFilter === EChannelProductAgFilter.UNMAPPED ? '-1' : '0';
    }

    return {
      limit: limit.toString(),
      page: page.toString(),
      sortDirection,
      sortField,
      relations,
      ...filters,
    };
  }

  private mapSortField(sortField :string) :string {
    switch (sortField) {
      case EChannelProductAgField.SHOPIFY_SKU:
        return 'shopify_sku';
      case EChannelProductAgField.PRODUCT_NAME:
        return 'product_name';
      case EChannelProductAgField.PRODUCT_FLAG_STATUS:
        return 'product_flag_status';
      case EChannelProductAgField.SYNC_STATUS:
        return 'sync_status';
      case EChannelProductAgField.CHANNEL:
        return 'channel';
      default:
        return 'id';
    }
  }

  private attachEventListenersOnPageStateChange() :void {
    // attach event listeners on filter, sort, column visibility change
    this.gridOptions.api?.addEventListener(
      'filterChanged',
      this.savePageState.bind(this),
    );
    this.gridOptions.api?.addEventListener(
      'sortChanged',
      this.savePageState.bind(this),
    );
    this.gridOptions.api?.addEventListener(
      'columnVisible',
      this.savePageState.bind(this),
    );
    this.gridOptions.api?.addEventListener(
      'columnPinned',
      this.savePageState.bind(this),
    );
  }

  private savePageState() :void {
    const filterModel = this.gridOptions.api?.getFilterModel();
    const columnState = this.gridOptions.columnApi?.getColumnState();
    const pinnedColumns = {
      left: this.gridOptions.columnApi
        ?.getDisplayedLeftColumns()
        .map((column) => column.getColId()),
      right: this.gridOptions.columnApi
        ?.getDisplayedRightColumns()
        .map((column) => column.getColId()),
    };

    const pageState = {
      filterModel,
      columnState,
      pinnedColumns,
    };

    localStorage.setItem(
      LocalStorageKeys.CHANNEL_PRODUCT_MAPPING_PAGE_STATE,
      JSON.stringify(pageState),
    );
  }

  private getPageState() :any {
    const pageState = localStorage.getItem(
      LocalStorageKeys.CHANNEL_PRODUCT_MAPPING_PAGE_STATE,
    );

    return pageState ? JSON.parse(pageState) : null;
  }

  private applySavedPageState() :void {
    if (this.getPageState()) {
      const pageState = this.getPageState();
      this.gridOptions.api?.setFilterModel(pageState.filterModel);
      this.gridOptions.columnApi?.applyColumnState({
        state: pageState.columnState,
      });

      if (pageState.pinnedColumns) {
        if (pageState.pinnedColumns.left?.length > 0) {
          this.gridOptions.columnApi?.setColumnPinned(
            pageState.pinnedColumns.left,
            'left',
          );
        }

        if (pageState.pinnedColumns.right?.length > 0) {
          this.gridOptions.columnApi?.setColumnPinned(
            pageState.pinnedColumns.right,
            'right',
          );
        }
      }
    }
  }

  onDownloadChannelProductMapping() :void {
    if (!this.latestRequestParams || this.downloadingState) {
      return;
    }
    this.downloadingState = true;
    this.loadingDialogUtilService.show('Downloading channel product mapping');
    const params = this.mapRequestParams(this.latestRequestParams);
    params.responseFormat = 'excel';
    this.channelProductMappingApiService
      .downloadProductsToExcel(params)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (response :Blob) => {
          this.downloadingState = false;
          this.loadingDialogUtilService.close();
          // Process the Excel file response
          const blob = new Blob([response], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          const downloadLink = document.createElement('a');
          downloadLink.href = window.URL.createObjectURL(blob);
          downloadLink.download = 'channel-product-mapping.xlsx';
          downloadLink.click();
        },
        (error) => {
          this.loadingDialogUtilService.close();
          this.downloadingState = false;
          // Handle errors
          console.error('Error downloading Excel file:', error);
        },
      );
  }
}
